'use client'

import { SortMode, SortOrder } from '@/lib/utils/filterUtils'
import { usePathname, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo, useState } from 'react'

export interface FilterState {
  search: string
  bookmarkedOnly: boolean
  tagIds: string[]
  sortMode: SortMode
  sortOrder: SortOrder | null
}

export interface FilterStateActions {
  setSearch: (value: string) => void
  setBookmarkedOnly: (value: boolean) => void
  toggleBookmark: () => void
  setTagIds: (tagIds: string[]) => void
  toggleTag: (tagId: string) => void
  setSortMode: (mode: SortMode) => void
  setSortOrder: (order: SortOrder) => void
  clearFilters: () => void
}

/**
 * Parse filter state from URL search params
 */
function parseStateFromParams(searchParams: URLSearchParams): FilterState {
  const search = searchParams.get('search') || ''
  const bookmarkedOnly = searchParams.get('bookmarked') === 'true'
  const tagIdsParam = searchParams.get('tags')
  const tagIds = tagIdsParam ? tagIdsParam.split(',').filter(Boolean) : []
  const sortMode = (searchParams.get('sort') as SortMode) || 'Last Edited'
  const sortOrderParam = searchParams.get('order')
  const sortOrder = sortOrderParam ? (sortOrderParam as SortOrder) : 'Descending'

  return {
    search,
    bookmarkedOnly,
    tagIds,
    sortMode,
    sortOrder,
  }
}

/**
 * Custom hook for managing filter state with browser history (undo/redo) support
 * Uses History API directly to avoid triggering Next.js navigation and refetches
 *
 * Benefits:
 * - Browser back/forward buttons work (undo/redo)
 * - URL updates for shareability
 * - NO component re-renders or refetches on filter changes
 * - Local state for instant updates
 */
export function useFilterState(): [FilterState, FilterStateActions] {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  // Local state for instant updates (source of truth)
  const [state, setState] = useState<FilterState>(() => parseStateFromParams(searchParams))

  // Sync state from URL on initial load and when navigating (back/forward)
  useEffect(() => {
    const handlePopState = () => {
      // When user clicks back/forward, sync our state from URL
      const params = new URLSearchParams(window.location.search)
      setState(parseStateFromParams(params))
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  // Helper to update URL using History API (doesn't trigger navigation)
  const updateUrl = useCallback(
    (updates: Partial<Record<string, string | null | undefined>>) => {
      const params = new URLSearchParams(window.location.search)

      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === '' || value === undefined) {
          params.delete(key)
        } else {
          params.set(key, value)
        }
      })

      const queryString = params.toString()
      const newUrl = queryString ? `${pathname}?${queryString}` : pathname
      const currentUrl = window.location.pathname + window.location.search

      // Only push to history if URL actually changed
      if (newUrl !== currentUrl) {
        window.history.pushState(null, '', newUrl)
      }
    },
    [pathname]
  )

  const actions = useMemo<FilterStateActions>(
    () => ({
      setSearch: (value: string) => {
        setState((prev) => ({ ...prev, search: value }))
        updateUrl({ search: value || null })
      },

      setBookmarkedOnly: (value: boolean) => {
        setState((prev) => ({ ...prev, bookmarkedOnly: value }))
        updateUrl({ bookmarked: value ? 'true' : null })
      },

      toggleBookmark: () => {
        setState((prev) => {
          const newValue = !prev.bookmarkedOnly
          updateUrl({ bookmarked: newValue ? 'true' : null })
          return { ...prev, bookmarkedOnly: newValue }
        })
      },

      setTagIds: (tagIds: string[]) => {
        setState((prev) => ({ ...prev, tagIds }))
        updateUrl({ tags: tagIds.length > 0 ? tagIds.join(',') : null })
      },

      toggleTag: (tagId: string) => {
        setState((prev) => {
          const newTagIds = prev.tagIds.includes(tagId)
            ? prev.tagIds.filter((id) => id !== tagId)
            : [...prev.tagIds, tagId]
          updateUrl({ tags: newTagIds.length > 0 ? newTagIds.join(',') : null })
          return { ...prev, tagIds: newTagIds }
        })
      },

      setSortMode: (mode: SortMode) => {
        setState((prev) => {
          const updates: Record<string, string | null> = {
            sort: mode === 'Last Edited' ? null : mode,
          }

          // Set default sort order based on mode
          let newOrder = prev.sortOrder
          if (mode === 'Alphabetical') {
            newOrder = 'Ascending'
            updates.order = 'Ascending'
          } else if (['Last Edited', 'Created'].includes(mode)) {
            newOrder = prev.sortOrder === 'Descending' ? 'Descending' : prev.sortOrder
            updates.order = newOrder === 'Descending' ? null : newOrder
          } else if (mode === 'Default') {
            newOrder = null
            updates.order = null
          }

          updateUrl(updates)
          return { ...prev, sortMode: mode, sortOrder: newOrder }
        })
      },

      setSortOrder: (order: SortOrder) => {
        setState((prev) => {
          // Only allow changing order if a mode that supports ordering is active
          if (prev.sortMode !== 'Default') {
            updateUrl({ order: order === 'Descending' ? null : order })
            return { ...prev, sortOrder: order }
          }
          return prev
        })
      },

      clearFilters: () => {
        setState((prev) => ({
          ...prev,
          search: '',
          bookmarkedOnly: false,
          tagIds: [],
        }))
        updateUrl({
          search: null,
          bookmarked: null,
          tags: null,
        })
      },
    }),
    [updateUrl]
  )

  return [state, actions]
}
